import { InventoryAllocation, Item, OptimizationLogEntry, Order_Output as Order, Product_Output, ProductionTask_Input } from "@/api"
import SeparablePanel from "@/components/SeparablePanel"
import { Checkbox } from "primereact/checkbox"
import { Column } from "primereact/column"
import { Message } from "primereact/message"
import { useMemo, useState } from "react"
import { extractToNumbersFromNumberWithBold, NumberWithBold } from "../types"
import EchoOrderProductionChart from "./charts/EchoOrderProductionChart"
import ItemMasterDetail from "./ItemMasterDetail"
import EchoItemOrderDataTable from "./EchoItemOrderDataTable"
import EchoProductionTaskDataTable from "./EchoProductionTaskDataTable"
import { ItemStats } from "./ItemTreeTable"

interface ItemDetailProps {
  items: Item[],
  dates: string[]
  itemStats: Record<Item["品目ID"], ItemStats>
  inventories: Record<Item["品目ID"], number[]>
  shipments: Record<Item["品目ID"], number[]>
  productions: Record<Item["品目ID"], NumberWithBold[]>;
  orders: Order[];
  productInventory: Record<Item["品目ID"], number[]>;
  productionTasks: ProductionTask_Input[];
  compareOrders?: Order[];
  compareProductionTasks?: ProductionTask_Input[];
  compareProductInventory?: Record<Item["品目ID"], number[]>;
  compareDates?: string[];
  optimizationLogs?: OptimizationLogEntry[];
  inventoryAllocations?: InventoryAllocation[];
  productsById: Record<string, Product_Output>
  onProductionChange?: (itemId: string, dateIndex: number, value: number, isBold: boolean) => void;
  // 最適化対象期間 (OptimizePlanResponseのhorizonStart/horizonEnd)
  horizonStart?: string | null;
  horizonEnd?: string | null;
  // 各データソースを区別するための追加データ
  excelProductions?: Record<Item["品目ID"], NumberWithBold[]>; // Excel由来の生産数
  optimizerProductions?: Record<Item["品目ID"], NumberWithBold[]>; // Optimizer由来の生産数
  userProductions?: Record<Item["品目ID"], Record<number, NumberWithBold>>; // ユーザー入力の生産数
}

const ItemDetail = ({
  items,
  itemStats,
  inventories,
  shipments,
  productions,
  orders,
  productInventory,
  productionTasks,
  compareOrders,
  compareProductionTasks,
  compareProductInventory,
  compareDates,
  optimizationLogs,
  inventoryAllocations,
  dates,
  productsById,
  onProductionChange,
  excelProductions,
  optimizerProductions,
  userProductions,
  horizonStart,
  horizonEnd,
}: ItemDetailProps) => {
  const [selectedItems, setSelectedItems] = useState<Item[]>([])
  const [compare, setCompare] = useState(false)
  const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([])
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null)
  const allocatedQuantityByOrderId = useMemo(() => {
    const acc: Record<string, number> = {}
    for (const task of productionTasks) {
      for (const alloc of task.order_allocations ?? []) {
        acc[alloc.order_id] = (acc[alloc.order_id] ?? 0) + (alloc.quantity ?? 0)
      }
    }
    return acc
  }, [productionTasks])
  const lateOrderReasonByOrderId = useMemo(() => {
    const acc: Record<string, string> = {}
    for (const task of productionTasks) {
      for (const alloc of task.order_allocations ?? []) {
        if (!alloc.reason) continue
        if (!alloc.reason.includes("納期遅れ")) continue
        const existing = acc[alloc.order_id]
        if (!existing || alloc.reason.length > existing.length) {
          acc[alloc.order_id] = alloc.reason
        }
      }
    }
    return acc
  }, [productionTasks])
  const inventoryAllocatedQuantityByOrderId = useMemo(() => {
    const acc: Record<string, number> = {}
    for (const alloc of inventoryAllocations ?? []) {
      acc[alloc.order_id] = (acc[alloc.order_id] ?? 0) + (alloc.quantity ?? 0)
    }
    return acc
  }, [inventoryAllocations])
  const productionTaskOrderIds = useMemo(() => {
    return new Set(Object.keys(allocatedQuantityByOrderId))
  }, [allocatedQuantityByOrderId])
  const filteredSelectedItems = useMemo(() => {
    return selectedItems.length > 0 ? selectedItems : items
  }, [selectedItems, items])
  const filteredSelectedItemSummary = useMemo(() => {
    if (filteredSelectedItems.length === 0) return []
    const summary = {
      '注文数合計': filteredSelectedItems.reduce((sum, item) => sum + itemStats[item.品目ID].総注文数, 0),
      '成形数合計': filteredSelectedItems.reduce((sum, item) => sum + itemStats[item.品目ID].総成形数, 0),
      // '生産回数（のべ）': filteredSelectedItems.map(item => item.生産日).flat().length,
      // '生産日': [...new Set(filteredSelectedItems.map(item => item.生産日).flat())].length,
      // '工程時間合計': filteredSelectedItems.reduce((sum, item) => sum + item.工程時間, 0),
    }
    return Object.entries(summary).map(([label, value]) => ({ label, value }))
  }, [filteredSelectedItems, itemStats])

  const masterProducts = useMemo(() => {
    return filteredSelectedItems
      .map((item) => productsById[item.品目ID])
      .filter((p): p is Product_Output => Boolean(p))
  }, [filteredSelectedItems, productsById])

  const filteredLogs = useMemo(() => {
    if (!optimizationLogs?.length) return []
    const skuIds = filteredSelectedItems.map((item) => item.品目ID)
    if (!skuIds.length) return []
    return optimizationLogs.filter((log) => skuIds.some((sku) => log.message?.includes(sku)))
  }, [optimizationLogs, filteredSelectedItems])


  const title = filteredSelectedItems.length > 0 ? (
    filteredSelectedItems.length > 1 ? `選択中品目(${filteredSelectedItems.length})の詳細` : `${filteredSelectedItems[0].品目ID}:${filteredSelectedItems[0].分類}の詳細`
  ) : "選択されていません"
  const chartOrders = compare ? (compareOrders ?? []) : orders
  const chartTasks = compare ? (compareProductionTasks ?? []) : productionTasks
  const chartDates = compare ? (compareDates ?? []) : dates
  const chartInventory = compare ? (compareProductInventory ?? {}) : productInventory
  return (
    <div className="flex flex-column gap-2">
      <div className="py-2">
        <ItemMasterDetail
          products={masterProducts}
          itemStats={itemStats}
          productionTasks={productionTasks}
          onSelectionChange={(products) => {
            const idSet = new Set(products.map((p) => p.sku_id))
            setSelectedItems(items.filter((item) => idSet.has(item.品目ID)))
          }}
        />
      </div>
      <div className="py-2">
        <label className="flex flex-row gap-2 line-height-2">
          <Checkbox
          value={true}
          checked={compare}
          onChange={(e) => {
            console.log("compare", e.checked)
            setCompare(e.checked ?? false)
          }}
          />
          <span>Excel上の計画と比較する</span>
        </label>
      </div>
    <SeparablePanel toggleable header="注文タイムライン">
        {filteredSelectedItems.length > 10 ? (
          <Message severity="warn" text="選択された品目が多いため、グラフ表示を制限しています" />
        ) : (
          <EchoOrderProductionChart
            items={filteredSelectedItems}
            orders={chartOrders}
            dates={chartDates}
            productInventory={chartInventory}
            productionTasks={chartTasks}
          />
        )}
    </SeparablePanel>
    <SeparablePanel toggleable header="注文一覧">
      <EchoItemOrderDataTable
        items={filteredSelectedItems}
        orders={orders}
        highlightedOrderIds={selectedOrderIds}
        taskLinkedOrderIds={productionTaskOrderIds}
        allocatedQuantityByOrderId={allocatedQuantityByOrderId}
        inventoryAllocatedQuantityByOrderId={inventoryAllocatedQuantityByOrderId}
        lateOrderReasonByOrderId={lateOrderReasonByOrderId}
        horizonStart={horizonStart ?? undefined}
        horizonEnd={horizonEnd ?? undefined}
      />
    </SeparablePanel>
    <SeparablePanel toggleable header="成形一覧">
      <EchoProductionTaskDataTable
        items={filteredSelectedItems}
        tasks={productionTasks}
        productsById={productsById}
        selectedTaskId={selectedTaskId}
        onSelectTask={(taskId, orderIds) => {
          setSelectedTaskId(taskId)
          setSelectedOrderIds(orderIds)
        }}
      />
    </SeparablePanel>
    <SeparablePanel toggleable header="最適化ログ">
      {!optimizationLogs?.length ? (
        <Message severity="info" text="最適化ログがありません" />
      ) : filteredLogs.length === 0 ? (
        <Message severity="info" text="選択中品目に該当するログがありません" />
      ) : (
        <ul
          className="list-none m-0 p-0 flex flex-column gap-2 sep-max-10rem"
        >
          {filteredLogs.map((log, idx) => (
            <li key={`${log.timestamp}-${idx}`} className="flex flex-column gap-1">
              <span className={`text-sm ${log.level === 'error' ? 'text-red-600' : 'text-600'}`}>
                {log.level}: {log.message}
              </span>
            </li>
          ))}
        </ul>
      )}
    </SeparablePanel>
    </div>
  )
}

export default ItemDetail
